import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

// Schema: Contractor has stripeAccountId, stripePayoutReady, stripePayoutReadyAt only
interface StripeConnectStatus {
  id: string;
  userId: string;
  company: string;
  stripeAccountId: string | null;
  stripePayoutReady?: boolean;
  stripePayoutReadyAt?: string | null;
  user: {
    email: string;
  };
}

interface StripeMetrics {
  totalAccounts: number;
  onboardedAccounts: number;
  chargesEnabled: number;
  payoutsEnabled: number;
  pendingRequirements: number;
}

const StripeConnectStatusTracking: React.FC = () => {
  const [contractors, setContractors] = useState<StripeConnectStatus[]>([]);
  const [metrics, setMetrics] = useState<StripeMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'onboarded' | 'pending' | 'issues'>('all');
  const { get, post } = useApi();

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      setLoading(true);
      const [contractorsResponse, metricsResponse] = await Promise.all([
        get('/api/admin/contractors/stripe-status'),
        get('/api/admin/contractors/stripe-metrics')
      ]);

      if (contractorsResponse.success) {
        setContractors(contractorsResponse.data);
      } else {
        setError(contractorsResponse.message || 'Failed to fetch contractors');
      }

      if (metricsResponse.success) {
        setMetrics(metricsResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForcePayoutReady = async (contractorId: string, company: string) => {
    if (!window.confirm(`Force-mark "${company}" as payout-ready? Only use this for test accounts where Stripe onboarding cannot be completed.`)) return;
    try {
      const response = await post('/api/admin/contractors/force-payout-ready', { contractorId });
      if (response.success) {
        fetchStripeStatus();
      } else {
        setError(response.message || 'Failed to force payout ready');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to force payout ready');
    }
  };

  const handleResendOnboardingLink = async (contractorId: string) => {
    try {
      const response = await post('/api/admin/contractors/resend-onboarding', {
        contractorId
      });

      if (response.success) {
        // Refresh the data
        fetchStripeStatus();
      } else {
        setError(response.message || 'Failed to resend onboarding link');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend onboarding link');
    }
  };

  const filteredContractors = contractors.filter(contractor => {
    switch (filter) {
      case 'onboarded':
        return !!(contractor.stripeAccountId && contractor.stripePayoutReady);
      case 'pending':
        return !!(contractor.stripeAccountId && !contractor.stripePayoutReady);
      case 'issues':
        return !!(contractor.stripeAccountId && !contractor.stripePayoutReady);
      default:
        return true;
    }
  });

  const getStatusColor = (contractor: StripeConnectStatus) => {
    if (!contractor.stripeAccountId) return 'bg-gray-100 text-gray-800';
    if (contractor.stripePayoutReady) return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (contractor: StripeConnectStatus) => {
    if (!contractor.stripeAccountId) return 'No Account';
    if (contractor.stripePayoutReady) return 'Fully Onboarded';
    return 'Pending Onboarding';
  };

  if (loading) return <div className="p-6">Loading Stripe Connect status...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Stripe Connect Status Tracking</h2>
        <p className="text-gray-600">Monitor contractor Stripe Connect account status and onboarding progress</p>
      </div>

      {/* Stripe Metrics Overview */}
      {metrics && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-blue-600">{metrics.totalAccounts}</div>
            <div className="text-sm text-gray-500">Total Accounts</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-green-600">{metrics.onboardedAccounts}</div>
            <div className="text-sm text-gray-500">Onboarded</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-purple-600">{metrics.chargesEnabled}</div>
            <div className="text-sm text-gray-500">Charges Enabled</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-orange-600">{metrics.payoutsEnabled}</div>
            <div className="text-sm text-gray-500">Payouts Enabled</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-red-600">{metrics.pendingRequirements}</div>
            <div className="text-sm text-gray-500">Pending Requirements</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { key: 'all', label: 'All', count: contractors.length },
            { key: 'onboarded', label: 'Onboarded', count: contractors.filter(c => c.stripeAccountId && c.stripePayoutReady).length },
            { key: 'pending', label: 'Pending', count: contractors.filter(c => c.stripeAccountId && !c.stripePayoutReady).length },
            { key: 'issues', label: 'Issues', count: contractors.filter(c => c.stripeAccountId && !c.stripePayoutReady).length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Contractors Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contractor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stripe Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requirements
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContractors.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No contractors found for the selected filter.
                  </td>
                </tr>
              ) : (
                filteredContractors.map((contractor) => (
                  <tr key={contractor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contractor.company || 'Contractor'}
                        </div>
                        <div className="text-sm text-gray-500">{contractor.user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contractor.stripeAccountId ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {contractor.stripeAccountId}
                          </div>
                          <div className="text-sm text-gray-500">
                            Type: Express
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No account created</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contractor)}`}>
                        {getStatusText(contractor)}
                      </span>
                      <div className="mt-1 space-y-1">
                        {contractor.stripePayoutReady && (
                          <div className="text-xs text-gray-500">
                            ✓ Payouts enabled
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">—</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col gap-1">
                        {contractor.stripeAccountId && !contractor.stripePayoutReady && (
                          <button
                            onClick={() => handleResendOnboardingLink(contractor.id)}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                          >
                            Resend Link
                          </button>
                        )}
                        {contractor.stripeAccountId && !contractor.stripePayoutReady && (
                          <button
                            onClick={() => handleForcePayoutReady(contractor.id, contractor.company || contractor.user.email)}
                            className="text-orange-600 hover:text-orange-900 bg-orange-50 hover:bg-orange-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                            title="Force-mark as payout-ready (test/unblocking only)"
                          >
                            Force Ready
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stripe Connect Information */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Stripe Connect Information</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>• <strong>Express Accounts:</strong> Quick onboarding with minimal information required</p>
          <p>• <strong>Onboarding Complete:</strong> Contractor has submitted all required information</p>
          <p>• <strong>Charges Enabled:</strong> Account can accept payments from customers</p>
          <p>• <strong>Payouts Enabled:</strong> Account can receive transfers from the platform</p>
          <p>• <strong>Requirements:</strong> Additional information or documents needed from Stripe</p>
          <p>• Contractors must complete onboarding before they can receive payments</p>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectStatusTracking;

